import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send, Loader2, AtSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteCard } from './NoteCard';
import {
  fetchNotes,
  createNote,
  fetchTeamMembers,
  type TeamMember,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface NotesSectionProps {
  entityType: 'candidate' | 'job' | 'application' | 'company';
  entityId: string;
  className?: string;
}

function NotesSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <Skeleton className="h-16 w-full" />
        </div>
      ))}
    </div>
  );
}

export function NotesSection({ entityType, entityId, className }: NotesSectionProps) {
  const { user } = useAuth();
  const [newNoteContent, setNewNoteContent] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch notes
  const {
    data: notesData,
    isLoading: isLoadingNotes,
  } = useQuery({
    queryKey: ['notes', entityType, entityId],
    queryFn: () => fetchNotes(entityType, entityId, { limit: 50 }),
    enabled: !!entityId,
  });

  // Fetch team members for mentions
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['teamMembers'],
    queryFn: fetchTeamMembers,
  });

  // Create note mutation
  const createMutation = useMutation({
    mutationFn: (content: string) => {
      // Extract mention user IDs from @[Name](userId) format
      const mentionMatches = content.match(/@\[([^\]]+)\]\(([^)]+)\)/g) || [];
      const mentions = mentionMatches.map((m) => {
        const idMatch = m.match(/@\[[^\]]+\]\(([^)]+)\)/);
        return idMatch ? idMatch[1] : '';
      }).filter(Boolean);
      return createNote(entityType, entityId, { content, mentions });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', entityType, entityId] });
      setNewNoteContent('');
    },
  });

  // Sort notes: pinned first, then by date
  const notes = notesData ?? [];
  const sortedNotes = [...notes].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Filter team members for mention dropdown
  const filteredMembers = teamMembers.filter((member) => {
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    return fullName.includes(mentionQuery.toLowerCase());
  });

  // Handle text input
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const position = e.target.selectionStart || 0;
    setNewNoteContent(value);
    setCursorPosition(position);

    // Check for @ mention trigger
    const textBeforeCursor = value.substring(0, position);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @ (still typing the mention)
      if (!/\s/.test(textAfterAt) || textAfterAt.split(' ').length <= 2) {
        setMentionQuery(textAfterAt);
        setShowMentionDropdown(true);
        return;
      }
    }
    setShowMentionDropdown(false);
    setMentionQuery('');
  }, []);

  // Insert mention
  const insertMention = useCallback((member: TeamMember) => {
    const textBeforeCursor = newNoteContent.substring(0, cursorPosition);
    const textAfterCursor = newNoteContent.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    const mentionText = `@[${member.firstName} ${member.lastName}](${member.id})`;
    const newContent = textBeforeCursor.substring(0, lastAtIndex) + mentionText + ' ' + textAfterCursor;

    setNewNoteContent(newContent);
    setShowMentionDropdown(false);
    setMentionQuery('');

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newCursorPos = lastAtIndex + mentionText.length + 1;
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [newNoteContent, cursorPosition]);

  // Close mention dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(event.target as Node) &&
        textareaRef.current !== event.target
      ) {
        setShowMentionDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionDropdown) {
      if (e.key === 'Escape') {
        setShowMentionDropdown(false);
        e.preventDefault();
      } else if (e.key === 'Tab' && filteredMembers.length > 0) {
        insertMention(filteredMembers[0]);
        e.preventDefault();
      }
    }

    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (newNoteContent.trim()) {
        createMutation.mutate(newNoteContent.trim());
      }
    }
  }, [showMentionDropdown, filteredMembers, insertMention, newNoteContent, createMutation]);

  const handleSubmit = () => {
    if (newNoteContent.trim()) {
      createMutation.mutate(newNoteContent.trim());
    }
  };

  if (!user) return null;

  return (
    <div className={cn('space-y-6', className)}>
      {/* New Note Form */}
      <div className="rounded-lg border border-white/10 bg-bg-tertiary p-4">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Add a note
        </h3>

        <div className="space-y-3">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={newNoteContent}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your note... Use @ to mention team members"
              className="min-h-[80px] resize-none"
              disabled={createMutation.isPending}
            />

            {/* Mention Dropdown */}
            {showMentionDropdown && filteredMembers.length > 0 && (
              <div
                ref={mentionDropdownRef}
                className="absolute z-50 mt-1 w-64 max-h-48 overflow-auto rounded-md border bg-bg-tertiary shadow-lg"
              >
                <div className="p-1">
                  {filteredMembers.slice(0, 5).map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => insertMention(member)}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-text-secondary hover:bg-white/10"
                    >
                      <AtSign className="h-4 w-4 text-text-tertiary" />
                      <span>
                        {member.firstName} {member.lastName}
                      </span>
                      <span className="text-xs text-text-tertiary ml-auto">
                        {member.email}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-text-tertiary">
              Press Ctrl+Enter to submit
            </p>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!newNoteContent.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Post Note
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Notes List */}
      <div>
        <h3 className="text-sm font-medium text-text-primary mb-3">
          Notes ({notes.length})
        </h3>

        {isLoadingNotes ? (
          <NotesSkeleton />
        ) : sortedNotes.length > 0 ? (
          <div className="space-y-3">
            {sortedNotes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                currentUserId={user.id}
                currentUserRole={user.role}
                entityType={entityType}
                entityId={entityId}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-text-tertiary border border-dashed border-white/20 rounded-lg">
            <MessageSquare className="h-8 w-8 mb-2" />
            <p className="text-sm">No notes yet</p>
            <p className="text-xs">Be the first to add a note</p>
          </div>
        )}
      </div>
    </div>
  );
}
